<template>
  <section class="sidebar">
    <section class="fullScreen" id="sidebar">
      <!-- ============================== Big Screen Sidebar -->
      <v-navigation-drawer
        class="main_sidebar"
        permanent
        :expand-on-hover="false"
        width="280"
      >
        <div class="sidebar__panel">
          <header class="sidebar__header">
            <router-link class="sidebar__brand" to="/">
              <img
                class="img-fluid"
                width="100"
                v-if="current_theme == 'light'"
                src="@/assets/media/images/logo/logo.png"
              />
              <img
                width="100"
                class="img-fluid"
                v-else
                src="@/assets/media/images/logo/logo.png"
              />
            </router-link>
          </header>

          <div class="sidebar__body">
            <v-list nav dense class="sidebar__list">
              <v-list-item class="sidebar__link sidebar__link--home" to="/">
                <div class="sidebar__link-content">
                  <span class="sidebar__badge">
                    <v-icon size="16">fas fa-home</v-icon>
                  </span>
                  <v-list-item-title class="font-weight-bold">{{
                    $t("navs.home")
                  }}</v-list-item-title>
                </div>
              </v-list-item>

              <div
                v-for="(item, index) in sidebar"
                :key="item.label || index"
                class="sidebar__section"
              >
                <div class="sidebar__section-group">
                  <template v-for="sub_item in item.categories">
                    <v-list-item
                      v-if="sub_item.key == 'general'"
                      class="sidebar__link"
                      :key="`general-${sub_item.id}`"
                      :to="`/${sub_item.permissions[0].url}`"
                    >
                      <div class="sidebar__link-content">
                        <span class="sidebar__badge">
                          <v-icon size="16">{{ sub_item.icon }}</v-icon>
                        </span>
                        <v-list-item-title class="font-weight-bold">
                          {{ sub_item.title }}
                        </v-list-item-title>
                      </div>
                    </v-list-item>

                    <v-list-group
                      v-else
                      class="sidebar__group"
                      :key="`group-${sub_item.id}`"
                      :class="{ 'is-open': sub_item.active }"
                      v-model="sub_item.active"
                      :append-icon="
                        sub_item.active ? 'mdi-chevron-up' : 'mdi-chevron-down'
                      "
                    >
                      <template v-slot:activator>
                        <div class="sidebar__link-content">
                          <span class="sidebar__badge">
                            <v-icon size="16">{{ sub_item.icon }}</v-icon>
                          </span>
                          <v-list-item-title class="font-weight-bold">
                            {{ sub_item.title }}
                          </v-list-item-title>
                        </div>
                      </template>

                      <v-list-item
                        v-for="child in sub_item.permissions"
                        :key="child.id"
                        class="sidebar__link sidebar__link--child"
                        :to="`/${child.url}`"
                      >
                        <div class="sidebar__link-content">
                          <span class="sidebar__dot"></span>
                          <v-list-item-title class="font-weight-bold">
                            {{ child.title }}
                          </v-list-item-title>
                        </div>
                      </v-list-item>
                    </v-list-group>
                  </template>
                </div>
              </div>
            </v-list>
          </div>

          <footer class="sidebar__foot" v-if="!isCollapsed">
            <div class="sidebar__foot-user">
              <span class="sidebar__foot-user-icon">
                <v-icon size="20">mdi-account-circle</v-icon>
              </span>
              <div class="sidebar__foot-user-text">
                <span class="sidebar__foot-user-text-name">{{ userName }}</span>
                <span class="sidebar__foot-user-text-role">{{ userRole }}</span>
              </div>
            </div>
          </footer>
        </div>
      </v-navigation-drawer>

      <!-- ============================== Small Screen Sidebar -->
    </section>
    <aside
      class="smallScreenSidebar main_sidebar"
      ref="smallSidebar"
      @click="toggleMenu"
    >
      <div class="sidebar__sheet" @click.stop>
        <span class="sidebar__sheet-handle"></span>
        <div class="sidebar__mobile-header">
          <router-link class="sidebar__brand" to="/">
            <img
              class="img-fluid"
              width="100"
              v-if="current_theme == 'light'"
              src="@/assets/media/images/logo/logo.png"
            />
            <img
              width="100"
              class="img-fluid"
              v-else
              src="@/assets/media/images/logo/logo.png"
            />
            <div class="sidebar__brand-text">
              <span class="sidebar__brand-title">{{ $t("dashName") }}</span>
            </div>
          </router-link>
          <button
            class="sidebar__sheet-close"
            @click="toggleMenu"
            type="button"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <v-list nav dense class="sidebar__list sidebar__list--mobile">
          <v-list-item class="sidebar__link" to="/">
            <div class="sidebar__link-content">
              <span class="sidebar__badge">
                <v-icon size="16">fas fa-home</v-icon>
              </span>
              <v-list-item-title class="font-weight-bold">{{
                $t("navs.home")
              }}</v-list-item-title>
            </div>
          </v-list-item>
          <div
            v-for="(item, index) in sidebar"
            :key="`mobile-${item.label || index}`"
            class="sidebar__section"
          >
            <div class="sidebar__section-group">
              <template v-for="sub_item in item.categories">
                <v-list-item
                  v-if="sub_item.key == 'general'"
                  class="sidebar__link"
                  :key="`mobile-general-${sub_item.id}`"
                  :to="`/${sub_item.permissions[0].url}`"
                >
                  <div class="sidebar__link-content">
                    <span class="sidebar__badge">
                      <v-icon size="16">{{ sub_item.icon }}</v-icon>
                    </span>
                    <v-list-item-title class="font-weight-bold">{{
                      sub_item.title
                    }}</v-list-item-title>
                  </div>
                </v-list-item>
                <v-list-group
                  v-else
                  class="sidebar__group"
                  :key="`mobile-group-${sub_item.id}`"
                  v-model="sub_item.active"
                  :append-icon="
                    sub_item.active ? 'mdi-chevron-up' : 'mdi-chevron-down'
                  "
                >
                  <template v-slot:activator>
                    <div class="sidebar__link-content">
                      <span class="sidebar__badge">
                        <v-icon size="16">{{ sub_item.icon }}</v-icon>
                      </span>
                      <v-list-item-title class="font-weight-bold">{{
                        sub_item.title
                      }}</v-list-item-title>
                    </div>
                  </template>

                  <v-list-item
                    v-for="child in sub_item.permissions"
                    :to="`/${child.url}`"
                    :key="child.id"
                    class="sidebar__link sidebar__link--child"
                  >
                    <div class="sidebar__link-content">
                      <span class="sidebar__dot"></span>
                      <v-list-item-title class="font-weight-bold">{{
                        child.title
                      }}</v-list-item-title>
                    </div>
                  </v-list-item>
                </v-list-group>
              </template>
            </div>
          </div>
        </v-list>

        <footer class="sidebar__foot sidebar__foot--mobile">
          <div class="sidebar__foot-user">
            <span class="sidebar__foot-user-icon">
              <v-icon size="20">mdi-account-circle</v-icon>
            </span>
            <div class="sidebar__foot-user-text">
              <span class="sidebar__foot-user-text-name">{{ userName }}</span>
              <span class="sidebar__foot-user-text-role">{{ userRole }}</span>
            </div>
          </div>
        </footer>
      </div>
    </aside>
  </section>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      sidebar: [],
    };
  },

  methods: {
    toggleMenu() {
      if (this.$refs.smallSidebar) {
        this.$refs.smallSidebar.classList.toggle("active");
      }
    },

    changeTheme(value) {
      this.$store.dispatch("theme_module/changeTheme", value);
      if (value == "light") {
        this.$vuetify.theme.dark = false;
      } else {
        this.$vuetify.theme.dark = true;
      }
      if (this.$refs.smallSidebar) {
        this.$refs.smallSidebar.classList.remove("active");
      }
    },

    changeLang(value) {
      this.$store.dispatch("lang_module/switchLang", value);
      if (this.$refs.smallSidebar) {
        this.$refs.smallSidebar.classList.remove("active");
      }
    },

    manipulateTheSideBar() {
      // Convert 'JSON' Object to JS Object
      const Json_Sidebar = JSON.parse(JSON.stringify(this.$t("sidebar")));

      Json_Sidebar.forEach((el) => {
        // IF Dropdown
        if (el.items) {
          // ======== Check Nested
          let inside_nested = false;
          el.items.forEach((element) => {
            if (element.items) {
              inside_nested = true;
            }
          });

          // ======== IF Nested Else
          if (inside_nested) {
            el.items.forEach((subEl) => {
              // Active
              subEl.activeRoutesMatch = subEl.items[0].url;

              // DropDown
              if (this.$route.path.includes(subEl.activeRoutesMatch)) {
                subEl.active = true;
              } else {
                subEl.active = false;
              }
            });
            el.activeRoutesMatch = el.parentUrl;

            // DropDown
            if (this.$route.path.includes(el.activeRoutesMatch)) {
              el.active = true;
            } else {
              el.active = false;
            }
          } else {
            el.activeRoutesMatch = el.items[0].url;

            // DropDown
            if (this.$route.path.includes(el.activeRoutesMatch)) {
              el.active = true;
            } else {
              el.active = false;
            }
          }
        }
      });

      this.sidebar = Json_Sidebar;
    },
    getSideBar() {
      this.axios.get("admin_side_bar").then((res) => {
        this.sidebar = res.data;
      });
    },
  },

  computed: {
    ...mapGetters({
      current_theme: "theme_module/current_theme",
      lang: "lang_module/lang",
      user: "auth_module/currentUser",
    }),
    userName() {
      return this.user?.full_name || this.user?.name || this.$t("navs.home");
    },
    userRole() {
      return this.user?.usertype || this.$t("navs.home");
    },
  },

  watch: {
    ["$route.path"]() {
      if (this.$refs.smallSidebar) {
        this.$refs.smallSidebar.classList.remove("active");
      }
    },
  },

  created() {
    this.control = this.$permission.setControls(this.$route.path);
    if (this.user.usertype == "superadmin") {
      this.manipulateTheSideBar();
    } else {
      this.getSideBar();
    }
  },
};
</script>

<style lang="scss">
// .main_sidebar.v-navigation-drawer {
//   width: 280px !important;
// }
</style>

